import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Box,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const bodyParts = [
  { label: 'Head', value: 'head' },
  { label: 'Face', value: 'face' },
  { label: 'Neck', value: 'neck' },
  { label: 'Left Shoulder', value: 'left_shoulder' },
  { label: 'Right Shoulder', value: 'right_shoulder' },
  { label: 'Left Upper Arm', value: 'left_upper_arm' },
  { label: 'Right Upper Arm', value: 'right_upper_arm' },
  { label: 'Left Elbow', value: 'left_elbow' },
  { label: 'Right Elbow', value: 'right_elbow' },
  { label: 'Left Forearm', value: 'left_forearm' },
  { label: 'Right Forearm', value: 'right_forearm' },
  { label: 'Left Wrist', value: 'left_wrist' },
  { label: 'Right Wrist', value: 'right_wrist' },
  { label: 'Left Hand', value: 'left_hand' },
  { label: 'Right Hand', value: 'right_hand' },
  { label: 'Left Thumb', value: 'left_thumb' },
  { label: 'Right Thumb', value: 'right_thumb' },
  { label: 'Left Fingers', value: 'left_fingers' },
  { label: 'Right Fingers', value: 'right_fingers' },
  { label: 'Chest', value: 'chest' },
  { label: 'Rib Cage', value: 'rib_cage' },
  { label: 'Abdomen', value: 'abdomen' },
  { label: 'Pelvis', value: 'pelvis' },
  { label: 'Groin', value: 'groin' },
  { label: 'Back', value: 'back' },
  { label: 'Upper Back', value: 'upper_back' },
  { label: 'Mid Back', value: 'mid_back' },
  { label: 'Lower Back', value: 'lower_back' },
  { label: 'Left Hip', value: 'left_hip' },
  { label: 'Right Hip', value: 'right_hip' },
  { label: 'Left Thigh', value: 'left_thigh' },
  { label: 'Right Thigh', value: 'right_thigh' },
  { label: 'Left Knee', value: 'left_knee' },
  { label: 'Right Knee', value: 'right_knee' },
  { label: 'Left Lower Leg', value: 'left_lower_leg' },
  { label: 'Right Lower Leg', value: 'right_lower_leg' },
  { label: 'Left Ankle', value: 'left_ankle' },
  { label: 'Right Ankle', value: 'right_ankle' },
  { label: 'Left Foot', value: 'left_foot' },
  { label: 'Right Foot', value: 'right_foot' },
  { label: 'Left Toes', value: 'left_toes' },
  { label: 'Right Toes', value: 'right_toes' },
  { label: 'Other', value: 'other' },
];

const painIntensityOptions = Array.from({ length: 11 }, (_, i) => ({ label: i.toString(), value: i }));

const painTypeOptions = [
  { label: 'Adhesion', value: 'Adhesion' },
  { label: 'Rotation', value: 'Rotation' },
  { label: 'Pain', value: 'Pain' },
  { label: 'Hypertonicity', value: 'Hypertonicity' },
  { label: 'Spasm', value: 'Spasm' },
  { label: 'Inflammation', value: 'Inflammation' },
  { label: 'Tender Point', value: 'Tender Point' },
  { label: 'Elevation', value: 'Elevation' },
  { label: 'Trigger Point', value: 'Trigger Point' },
];

const frequencyOptions = [
  { label: '5 mins', value: '5 mins' },
  { label: '10 mins', value: '10 mins' },
  { label: '20 mins', value: '20 mins' },
  { label: '30 mins', value: '30 mins' },
  { label: '1 hour', value: '1 hour' },
  { label: '2 hours', value: '2 hours' },
  { label: '3 hours', value: '3 hours' },
  { label: '4 hours', value: '4 hours' },
];

interface WoundRecord {
  location: string;
  intensity: number;
  duration: string;
  frequency: string;
  onset: string;
  type: string;
}

interface WoundDialogProps {
  woundRecords: WoundRecord[];
  setWoundRecords: (records: WoundRecord[]) => void;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const WoundDialog: React.FC<WoundDialogProps> = ({
  woundRecords,
  setWoundRecords,
  open,
  onOpen,
  onClose,
}) => {
  const [newWound, setNewWound] = useState<WoundRecord>({
    location: '',
    intensity: 0,
    duration: '',
    frequency: '',
    onset: '',
    type: '',
  });

  const handleAddWound = () => {
    if (newWound.location && newWound.type) {
      setWoundRecords([...woundRecords, newWound]);
      setNewWound({
        location: '',
        intensity: 0,
        duration: '',
        frequency: '',
        onset: '',
        type: '',
      });
      onClose();
    }
  };
  const handleDeleteWound = (index: number) => {
    const updated = woundRecords.filter((_, i) => i !== index);
    setWoundRecords(updated);
  };

  const handleInputChange = (field: keyof WoundRecord, value: any) => {
    setNewWound((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{ backgroundColor: '#2f3347' }}>
            <TableRow>
              <TableCell style={{ color: 'white' }}>Actions</TableCell>
              <TableCell style={{ color: 'white' }}>Location</TableCell>
              <TableCell style={{ color: 'white' }}>Intensity</TableCell>
              <TableCell style={{ color: 'white' }}>Duration</TableCell>
              <TableCell style={{ color: 'white' }}>Frequency</TableCell>
              <TableCell style={{ color: 'white' }}>Onset</TableCell>
              <TableCell style={{ color: 'white' }}>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {woundRecords.length > 0 ? (
              woundRecords.map((wound, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <IconButton onClick={() => handleDeleteWound(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>{wound.location}</TableCell>
                  <TableCell>{wound.intensity}</TableCell>
                  <TableCell>{wound.duration}</TableCell>
                  <TableCell>{wound.frequency}</TableCell>
                  <TableCell>{wound.onset}</TableCell>
                  <TableCell>{wound.type}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  No Wound Records Added
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="contained" onClick={onOpen} startIcon={<AddIcon />} sx={{ mt: 2 }}>
        Add Wound Record
      </Button>

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Add New Wound Record</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="location-label">Location</InputLabel>
                <Select
                  labelId="location-label"
                  value={newWound.location}
                  label="Location"
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  required
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: 'auto',
                      },
                    },
                  }}
                >
                  {bodyParts.map((part) => (
                    <MenuItem key={part.value} value={part.value}>
                      {part.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="duration"
                label="Duration (minutes)"
                type="number"
                value={newWound.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                size="small"
                fullWidth
                required
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="frequency-label">Frequency</InputLabel>
                <Select
                  labelId="frequency-label"
                  value={newWound.frequency}
                  label="Frequency"
                  onChange={(e) => handleInputChange('frequency', e.target.value)}
                  required
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: 'auto',
                      },
                    },
                  }}
                >
                  {frequencyOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Onset"
                  value={newWound.onset ? dayjs(newWound.onset) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      handleInputChange('onset', newValue.format('MM-DD-YYYY'));
                    }
                  }}
                  shouldDisableDate={(date: any) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date > today;
                  }}
                  slotProps={{
                    field:{
                      readOnly: true,
                    },
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: true,
                      inputProps: { 
                        style: { cursor: 'pointer' },
                        readOnly: true,
                      },
                      onClick: (e) => {
                        // Prevent the click from bubbling up and trigger the date picker
                        e.preventDefault();
                        e.stopPropagation();
                        // Find and click the calendar icon to open the picker
                        const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                         e.currentTarget.querySelector('.MuiIconButton-root');
                        if (iconButton) {
                          (iconButton as HTMLElement).click();
                        }
                      }
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Intensity of Pain</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={newWound.intensity}
                  onChange={(e) => handleInputChange('intensity', Number(e.target.value))}
                >
                  {painIntensityOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Type of Pain</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={newWound.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  {painTypeOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddWound}
            disabled={!newWound.location || !newWound.type}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WoundDialog; 